// Lançamentos: todas as movimentações (abertura, entradas, saídas, ajustes) com filtros, ajuste
// avulso com motivo obrigatório e cancelamento (saída/ajuste; entrada se cancela pela NF).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, Plus, XCircle } from "lucide-react";
import { getEstoqueMovimentacoes, getEstoquePosicao, getEstoqueProdutos, getRoleFromToken, postEstoqueAjuste, postEstoqueCancelarMovimento } from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import EstoqueLayout from "./EstoqueLayout";
import {
  AvisoSemAbertura, JustificativaSaldoNegativo, MOTIVOS_AJUSTE, ModalCancelar, ProdutoSelect, TipoBadge, TIPOS_MOVIMENTO,
  formatData, hojeISO, mensagemErro, useContextoEstoque,
} from "./comum";
import { AvisoErro, Campo, Modal, NumeroInput, btnStyle, formatBRL, formatQtd, inputStyle, palette, parseNumero, useIsMobile } from "./ui";

function ModalAjuste({ C, contexto, produtos, onClose, onSalvo }) {
  const [form, setForm] = useState({ data: hojeISO(), produto_id: null, sentido: "-", quantidade: "", motivo_tipo: "PERDA", motivo: "" });
  const [saldo, setSaldo] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const produto = produtos.find((p) => p.id === form.produto_id);

  useEffect(() => {
    if (!form.produto_id) { setSaldo(null); return; }
    getEstoquePosicao(form.data).then((p) => {
      const linha = p.itens.find((i) => i.produto_id === form.produto_id);
      setSaldo(linha ? Number(linha.quantidade) : 0);
    }).catch(() => setSaldo(null));
  }, [form.produto_id, form.data]);

  async function salvar() {
    const q = parseNumero(form.quantidade);
    if (!form.produto_id || !q || Number(q) <= 0 || !form.motivo.trim()) {
      setErro(new Error("Preencha produto, quantidade maior que zero e motivo."));
      return;
    }
    setSalvando(true);
    try {
      await postEstoqueAjuste({
        data: form.data, produto_id: form.produto_id, quantidade: `${form.sentido === "-" ? "-" : ""}${q}`,
        motivo_tipo: form.motivo_tipo, motivo: form.motivo.trim(), justificativa_saldo_negativo: justificativa.trim() || null,
      });
      onSalvo();
    } catch (e) {
      setErro(e);
      setSalvando(false);
    }
  }

  const q = Number(parseNumero(form.quantidade)) || 0;
  const saldoDepois = saldo !== null ? saldo + (form.sentido === "-" ? -q : q) : null;
  return (
    <Modal C={C} titulo="Novo ajuste" onClose={onClose} largura={500}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
        <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.warning), opacity: salvando ? 0.6 : 1 }}>{salvando ? "Salvando..." : "Lançar ajuste"}</button>
      </>}>
      <p style={{ fontSize: 12, color: C.muted, marginTop: 0, lineHeight: 1.5 }}>
        Use para corrigir o saldo quando uma diferença for encontrada (perda, avaria, erro de lançamento). O ajuste é valorizado pelo custo médio vigente.
      </p>
      <AvisoErro C={C} onClose={() => setErro(null)}>{erro && mensagemErro(erro)}</AvisoErro>
      <JustificativaSaldoNegativo C={C} erro={erro} valor={justificativa} onChange={setJustificativa} />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          <Campo label="Data" C={C} obrigatorio><input type="date" value={form.data} min={contexto.data_abertura || undefined} max={hojeISO()} onChange={(e) => setForm({ ...form, data: e.target.value })} style={inputStyle(C)} /></Campo>
          <Campo label="Tipo de motivo" C={C} obrigatorio>
            <select value={form.motivo_tipo} onChange={(e) => setForm({ ...form, motivo_tipo: e.target.value })} style={inputStyle(C)}>
              {MOTIVOS_AJUSTE.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </Campo>
        </div>
        <Campo label="Produto" C={C} obrigatorio><ProdutoSelect C={C} produtos={produtos} value={form.produto_id} onChange={(id) => setForm({ ...form, produto_id: id })} /></Campo>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
            {[["-", "Falta (−)"], ["+", "Sobra (+)"]].map(([s, label]) => (
              <button key={s} onClick={() => setForm({ ...form, sentido: s })} style={{ padding: "10px 14px", minHeight: 42, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, background: form.sentido === s ? (s === "-" ? C.danger : C.success) : "transparent", color: form.sentido === s ? "#fff" : C.muted }}>{label}</button>
            ))}
          </div>
          <div style={{ flex: "1 1 120px" }}><Campo label={`Quantidade${produto ? ` (${produto.unidade_padrao_sigla})` : ""}`} C={C} obrigatorio><NumeroInput C={C} value={form.quantidade} onChange={(v) => setForm({ ...form, quantidade: v })} /></Campo></div>
        </div>
        {saldo !== null && (
          <div style={{ fontSize: 13, background: C.bgActive, borderRadius: 8, padding: 10 }}>
            Saldo em {formatData(form.data)}: <strong>{formatQtd(saldo)}</strong> → depois do ajuste: <strong style={{ color: saldoDepois < 0 ? C.danger : C.text }}>{formatQtd(saldoDepois)}</strong> {produto?.unidade_padrao_sigla}
          </div>
        )}
        <Campo label="Motivo (descrição)" C={C} obrigatorio><input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ex.: 2 frascos vazaram no armário" style={inputStyle(C)} /></Campo>
      </div>
    </Modal>
  );
}

export default function MovimentacoesView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const perm = permissoesEstoque(getRoleFromToken());
  const { contexto, erro: erroContexto } = useContextoEstoque();
  const [produtos, setProdutos] = useState([]);
  const [filtros, setFiltros] = useState({ inicio: "", fim: "", tipo: "", produto_id: null });
  const [movs, setMovs] = useState([]);
  const [erro, setErro] = useState("");
  const [modalAjuste, setModalAjuste] = useState(false);
  const [cancelando, setCancelando] = useState(null);

  const carregar = useCallback(() => getEstoqueMovimentacoes(filtros).then(setMovs).catch((e) => setErro(e.message)), [filtros]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { getEstoqueProdutos().then(setProdutos).catch((e) => setErro(e.message)); }, []);

  const podeCancelar = (m) => perm.financeiro && m.status === "ATIVA" && (m.tipo === "SAIDA" || (m.tipo === "AJUSTE" && m.documento?.tipo !== "INVENTARIO"));

  return (
    <EstoqueLayout titulo="Lançamentos" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro || erroContexto}</AvisoErro>
      {!contexto ? null : !contexto.abertura_concluida ? <AvisoSemAbertura C={C} /> : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
            <div style={{ flex: "0 1 150px" }}><Campo label="De" C={C}><input type="date" value={filtros.inicio} onChange={(e) => setFiltros({ ...filtros, inicio: e.target.value })} style={inputStyle(C)} /></Campo></div>
            <div style={{ flex: "0 1 150px" }}><Campo label="Até" C={C}><input type="date" value={filtros.fim} onChange={(e) => setFiltros({ ...filtros, fim: e.target.value })} style={inputStyle(C)} /></Campo></div>
            <div style={{ flex: "0 1 140px" }}>
              <Campo label="Tipo" C={C}>
                <select value={filtros.tipo} onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })} style={inputStyle(C)}>
                  <option value="">Todos</option>
                  {Object.entries(TIPOS_MOVIMENTO).map(([id, t]) => <option key={id} value={id}>{t.label}</option>)}
                </select>
              </Campo>
            </div>
            <div style={{ flex: "1 1 220px" }}><Campo label="Produto" C={C}><ProdutoSelect C={C} produtos={produtos} value={filtros.produto_id} onChange={(id) => setFiltros({ ...filtros, produto_id: id })} /></Campo></div>
            {perm.financeiro && <button onClick={() => setModalAjuste(true)} style={{ ...btnStyle(C.warning), marginLeft: "auto" }}><Plus size={15} /> Novo ajuste</button>}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{movs.length} lançamento(s){movs.length >= 500 ? " (mostrando os 500 mais recentes — refine os filtros)" : ""}</div>

          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {movs.map((m) => (
                <div key={m.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, opacity: m.status === "CANCELADA" ? 0.5 : 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <TipoBadge tipo={m.tipo} cancelado={m.status === "CANCELADA"} />
                    <span style={{ fontSize: 12, color: C.muted }}>{formatData(m.data)}</span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700 }}>{formatBRL(m.valor_total)}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{formatQtd(m.quantidade)} {m.unidade_sigla} · {m.produto_nome}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{m.documento?.rotulo || m.motivo || m.destino || m.responsavel_nome}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <button onClick={() => navigate(`/estoque/kardex?produto=${m.produto_id}`)} style={{ ...btnStyle(C.brand, true), padding: "6px 10px", minHeight: 34, fontSize: 12 }}><History size={13} /> Extrato</button>
                    {podeCancelar(m) && <button onClick={() => setCancelando(m)} style={{ ...btnStyle(C.danger, true), padding: "6px 10px", minHeight: 34, fontSize: 12 }}>Cancelar</button>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "68vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Data", "Tipo", "Produto", "Quantidade", "Custo unit.", "Valor", "Documento / motivo", "Responsável", ""].map((h, i) => (
                      <th key={i} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", textAlign: [3, 4, 5].includes(i) ? "right" : "left", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", position: "sticky", top: 0, background: C.card }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movs.length === 0 && <tr><td colSpan={9} style={{ padding: 24, textAlign: "center", color: C.muted }}>Nenhum lançamento.</td></tr>}
                  {movs.map((m) => {
                    const cancelado = m.status === "CANCELADA";
                    const td = (align = "left") => ({ padding: "8px 12px", borderBottom: `1px solid ${C.border}`, textAlign: align, opacity: cancelado ? 0.5 : 1, whiteSpace: align === "right" ? "nowrap" : "normal" });
                    return (
                      <tr key={m.id} title={cancelado ? `Cancelado: ${m.motivo_cancelamento}` : m.saldo_negativo_justificativa ? `Saldo negativo justificado: ${m.saldo_negativo_justificativa}` : undefined}>
                        <td style={{ ...td(), whiteSpace: "nowrap" }}>{formatData(m.data)}</td>
                        <td style={td()}><TipoBadge tipo={m.tipo} cancelado={cancelado} /></td>
                        <td style={td()}>
                          <button onClick={() => navigate(`/estoque/kardex?produto=${m.produto_id}`)} style={{ background: "none", border: "none", padding: 0, color: C.text, cursor: "pointer", fontFamily: "inherit", fontSize: 13, textAlign: "left" }}>{m.produto_nome}</button>
                        </td>
                        <td style={td("right")}>{formatQtd(m.quantidade)} {m.unidade_sigla}</td>
                        <td style={td("right")}>{formatBRL(m.custo_unitario)}</td>
                        <td style={{ ...td("right"), fontWeight: 700 }}>{formatBRL(m.valor_total)}</td>
                        <td style={{ ...td(), color: C.muted, maxWidth: 260 }}>
                          {m.documento?.rotulo || [MOTIVOS_AJUSTE.find((x) => x.id === m.motivo_tipo)?.label, m.motivo].filter(Boolean).join(": ") || m.destino || "—"}
                          {cancelado && <div style={{ color: C.danger, fontSize: 11 }}>Cancelado: {m.motivo_cancelamento}</div>}
                        </td>
                        <td style={{ ...td(), color: C.muted }}>{m.responsavel_nome || "—"}</td>
                        <td style={{ ...td("right"), opacity: 1 }}>
                          {podeCancelar(m) && (
                            <button onClick={() => setCancelando(m)} aria-label="Cancelar lançamento" title="Cancelar lançamento" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", display: "flex", padding: 4 }}>
                              <XCircle size={17} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {modalAjuste && <ModalAjuste C={C} contexto={contexto} produtos={produtos.filter((p) => p.ativo)} onClose={() => setModalAjuste(false)} onSalvo={() => { setModalAjuste(false); carregar(); }} />}
      {cancelando && (
        <ModalCancelar C={C} titulo={`Cancelar ${TIPOS_MOVIMENTO[cancelando.tipo].label.toLowerCase()}`} onClose={() => setCancelando(null)}
          descricao={`${formatQtd(cancelando.quantidade)} ${cancelando.unidade_sigla} de ${cancelando.produto_nome} em ${formatData(cancelando.data)}. O registro continua no histórico, mas deixa de contar no saldo.`}
          onConfirmar={async (dados) => { await postEstoqueCancelarMovimento(cancelando.id, dados); setCancelando(null); carregar(); }} />
      )}
    </EstoqueLayout>
  );
}
